import React from "react";
import LoaderButton from "../LoaderButton";

export const SCORE_COLUMN_NAME = "Text Search Score";

/**
 * Index Search criteria: one input per field of the selected index template (text, controlled list or date range),
 * plus the optional OCR text filter, exactly as the previous search screen built them, but as controlled inputs.
 * With "All index templates" chosen it is the Block & Lot form instead: the two fields every property template
 * shares, searched across all of them (POST api/ChatSearch/block-lot-search).
 */
export default function IndexSearchPanel({
  columns,
  search,
  templateSelected,
  templateDisplayName,
  showTextSearch,
  isLoading,
  readOnly,
  onChange,
  onSubmit,
  onReset,
  blockLot,
  blockLotError,
  onBlockLotChange,
  onBlockLotSubmit,
}) {
  if (!templateSelected) {
    const values = blockLot || {};
    return (
      <form
        id="blockLotControls"
        className="ds-panel ds-criteria"
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          if (!readOnly) onBlockLotSubmit();
        }}
      >
        <div className="ds-panel-heading">
          <h2>Block &amp; Lot</h2>
          <p>
            Searches the Block and Lot fields of every index template that has
            them. Choose one template in the scope bar to search its own fields.
          </p>
        </div>
        <div className="ds-field">
          <label htmlFor="idx-block">Block</label>
          <input
            id="idx-block"
            type="text"
            maxLength={100}
            className="ds-input input-box"
            value={values.block || ""}
            onChange={(event) => onBlockLotChange("block", event.target.value)}
          />
        </div>
        <div className="ds-field">
          <label htmlFor="idx-lot">Lot</label>
          <input
            id="idx-lot"
            type="text"
            maxLength={100}
            className="ds-input input-box"
            value={values.lot || ""}
            onChange={(event) => onBlockLotChange("lot", event.target.value)}
          />
        </div>
        {blockLotError && (
          <p className="ds-hint ds-error" role="alert">
            {blockLotError}
          </p>
        )}
        <div className="ds-actions">
          <LoaderButton
            block
            bsClass="btn"
            disabled={readOnly}
            type="submit"
            isLoading={isLoading}
            text="Search"
            loadingText="Searching..."
            className="ds-btn-primary searchResetbutton"
          />
          <button
            type="button"
            className="btn ds-btn-secondary searchResetbutton"
            onClick={onReset}
          >
            Reset
          </button>
        </div>
      </form>
    );
  }

  const fields = (columns || []).filter(
    (column) => column && column.InputName !== SCORE_COLUMN_NAME,
  );
  const value = (key) => (search && search[key] !== undefined ? search[key] : "");

  return (
    <form
      id="searchControls"
      className="ds-panel ds-criteria"
      autoComplete="off"
      onSubmit={(event) => {
        event.preventDefault();
        if (!readOnly) onSubmit();
      }}
    >
      <div className="ds-panel-heading">
        <h2>Search criteria</h2>
        <p>
          {`Fields come from the ${templateDisplayName} index template.`}
        </p>
      </div>

      {fields.map((column) => {
        const id = column.InputName;
        if (column.ControlledList) {
          const options = [id].concat(column.ControlledList.slice().sort());
          return (
            <div className="ds-field" key={id}>
              <label htmlFor={`idx-${id}`}>{id}</label>
              <select
                id={`idx-${id}`}
                className="ds-select custom-select"
                value={value(id) || id}
                onChange={(event) => onChange(id, event.target.value)}
              >
                {options.map((item, index) => (
                  <option key={item} value={item}>
                    {index === 0 ? "Any" : item}
                  </option>
                ))}
              </select>
            </div>
          );
        }
        if (column.DataType === "date") {
          return (
            <div className="ds-field-row" key={id}>
              <div className="ds-field">
                <label htmlFor={`idx-fromDate_${id}`}>{id} from</label>
                <input
                  id={`idx-fromDate_${id}`}
                  type="date"
                  className="ds-input input-box date"
                  value={value(`fromDate_${id}`)}
                  onChange={(event) =>
                    onChange(`fromDate_${id}`, event.target.value)
                  }
                />
              </div>
              <div className="ds-field">
                <label htmlFor={`idx-toDate_${id}`}>{id} to</label>
                <input
                  id={`idx-toDate_${id}`}
                  type="date"
                  className="ds-input input-box date"
                  value={value(`toDate_${id}`)}
                  onChange={(event) =>
                    onChange(`toDate_${id}`, event.target.value)
                  }
                />
              </div>
            </div>
          );
        }
        return (
          <div className="ds-field" key={id}>
            <label htmlFor={`idx-${id}`}>{id}</label>
            <input
              id={`idx-${id}`}
              type="text"
              className="ds-input input-box"
              value={value(id)}
              onChange={(event) => onChange(id, event.target.value)}
            />
          </div>
        );
      })}

      {showTextSearch && (
        <div className="ds-field ds-field-text-filter">
          <label htmlFor="idx-textSearch">
            Document text contains{" "}
            <span className="ds-muted">(optional, OCR)</span>
          </label>
          <input
            id="idx-textSearch"
            type="text"
            className="ds-input input-box ds-text-filter"
            placeholder="words inside the documents"
            value={value("textSearch")}
            onChange={(event) => onChange("textSearch", event.target.value)}
          />
          <p className="ds-hint">
            Narrows the index results by OCR text and adds a Text Search Score
            column.
          </p>
        </div>
      )}

      <div className="ds-actions">
        <LoaderButton
          block
          bsClass="btn"
          disabled={readOnly}
          type="submit"
          isLoading={isLoading}
          text="Search"
          loadingText="Searching..."
          className="ds-btn-primary searchResetbutton"
        />
        <button
          type="button"
          className="btn ds-btn-secondary searchResetbutton"
          onClick={onReset}
        >
          Reset
        </button>
      </div>
    </form>
  );
}
